import { fontFamilyCss } from '../fontStack.js'

/**
 * Text metrics for the chart renderer, in points. Widths come from the
 * browser (canvas measureText, the same font resolution the SVG text gets);
 * vertical metrics from the fonts' own tables, because Office stacks chart
 * text lines by ascent + descent + line gap of the face, not by CSS
 * line-height. Metric-compatible substitutes share their original's numbers.
 */

const PX_PER_PT = 96 / 72

/**
 * ascent, descent, line gap as fractions of the em (hhea/OS2 win metrics),
 * `win` = usWinAscent + usWinDescent where it differs from their sum,
 * and where Office puts the baseline of a chart text line: `top` below the
 * top of a top-anchored line, `center` below the centre of a centred one
 * (fitted on Word's renders of the prod malls' charts for Arial and
 * Calibri, the two faces they use; the rest follow from the metrics).
 */
const FACE_METRICS = {
  arial: { ascent: 0.905, descent: 0.212, gap: 0.033, top: 0.938, center: 0.27, win: 1.117 },
  calibri: { ascent: 0.75, descent: 0.25, gap: 0.22, top: 1.048, center: 0.14, win: 1.221 },
  cambria: { ascent: 0.95, descent: 0.222, gap: 0, top: 0.95, center: 0.364 },
  times: { ascent: 0.891, descent: 0.216, gap: 0.042, top: 0.933, center: 0.338 },
  courier: { ascent: 0.833, descent: 0.3, gap: 0, top: 0.833, center: 0.267 },
}

const FACE_ALIASES = {
  arial: 'arial',
  arimo: 'arial',
  helvetica: 'arial',
  'liberation sans': 'arial',
  calibri: 'calibri',
  'calibri light': 'calibri',
  carlito: 'calibri',
  cambria: 'cambria',
  caladea: 'cambria',
  'times new roman': 'times',
  tinos: 'times',
  'liberation serif': 'times',
  'courier new': 'courier',
  cousine: 'courier',
}

export function faceMetrics(font) {
  const key = FACE_ALIASES[String(font ?? '').toLowerCase()]
  return FACE_METRICS[key] ?? FACE_METRICS.arial
}

/** Baseline offset from the top of a line box, points. */
export function textAscent(style) {
  return faceMetrics(style.font).ascent * style.size
}

/** Descent below the baseline, points. */
export function textDescent(style) {
  return faceMetrics(style.font).descent * style.size
}

/** Baseline below the top of a top-anchored line, points. */
export function baselineFromTop(style) {
  return faceMetrics(style.font).top * style.size
}

/** Baseline below the centre of a vertically centred line, points. */
export function baselineFromCenter(style) {
  return faceMetrics(style.font).center * style.size
}

/** One line's advance, points: what Office steps from line to line. */
export function lineHeight(style) {
  const face = faceMetrics(style.font)
  return (face.ascent + face.descent + face.gap) * style.size
}

/** The face's Windows height (usWinAscent + usWinDescent) at the style's size, points. */
export function faceHeight(style) {
  const face = faceMetrics(style.font)
  return (face.win ?? face.ascent + face.descent + face.gap) * style.size
}

/** The CSS font shorthand for a chart text style (canvas and SVG alike). */
export function fontShorthand(style) {
  const parts = []
  if (style.italic) parts.push('italic')
  if (style.bold) parts.push('bold')
  parts.push(`${style.size}pt`)
  parts.push(fontFamilyCss(style.font) || 'sans-serif')
  return parts.join(' ')
}

/**
 * Whether Office pair-kerns this text: only from its a:rPr@kern size up
 * (kern="1200" = 12 pt and larger); without the attribute, never. A 10 pt
 * title measured with kerning fit a line Word wraps (chart 45).
 */
export function kerns(style) {
  return style.kern > 0 && style.size >= style.kern
}

/**
 * A width measurer: (text, style) → points. Uses one shared 2D context;
 * where none exists (node tests) falls back to an average-advance estimate
 * so geometry stays deterministic there.
 */
export function createMeasure() {
  let context = null
  try {
    if (typeof OffscreenCanvas !== 'undefined') context = new OffscreenCanvas(1, 1).getContext('2d')
    else if (typeof document !== 'undefined') context = document.createElement('canvas').getContext('2d')
  } catch {
    context = null
  }
  const cache = new Map()
  return (text, style) => {
    if (!text) return 0
    const font = fontShorthand(style)
    const kerning = kerns(style)
    const key = font + (kerning ? '\u0001' : '\u0000') + text
    let width = cache.get(key)
    if (width === undefined) {
      if (context && typeof context.measureText === 'function') {
        context.font = font
        context.fontKerning = kerning ? 'normal' : 'none'
        width = context.measureText(text).width / PX_PER_PT
      } else {
        width = estimateWidth(text, style)
      }
      cache.set(key, width)
    }
    return width
  }
}

/** Average-advance estimate (Arial-like) for hosts without a canvas. */
export function estimateWidth(text, style) {
  let em = 0
  for (const char of text) {
    if (/[ il.,:;'|!]/.test(char)) em += 0.278
    else if (/[mwMW]/.test(char)) em += 0.833
    else if (/[A-Z]/.test(char)) em += 0.667
    else em += 0.556
  }
  return em * style.size * (style.bold ? 1.06 : 1)
}

/**
 * Greedy word wrap of `text` into lines no wider than `maxWidth` (points);
 * a single word wider than the line stays on its own line.
 */
export function wrapText(text, style, maxWidth, measure) {
  const lines = []
  for (const paragraph of String(text).split('\n')) {
    const words = paragraph.split(/ +/).filter((word, index, all) => word !== '' || all.length === 1)
    let line = ''
    for (const word of words) {
      const candidate = line === '' ? word : `${line} ${word}`
      if (line !== '' && measure(candidate, style) > maxWidth) {
        lines.push(line)
        line = word
      } else {
        line = candidate
      }
    }
    lines.push(line)
  }
  return lines
}
